import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';

/**
 * Firebase Web SDK initialiser.
 *
 * Reads from Vite env vars (VITE_FIREBASE_*) at build time. These are PUBLIC
 * values — the API key is not a secret and is safe to ship in the bundle; the
 * security boundary is Firebase Auth's domain-based restrictions configured
 * in the Firebase Console (Authentication → Settings → Authorized domains).
 *
 * Used only to perform the Email Action Code flow on desktop browsers:
 *   - applyActionCode(auth, oobCode)            ← verify email
 *   - confirmPasswordReset(auth, oobCode, pwd)  ← reset password
 *
 * On mobile, Universal Links / App Links route the user to the native app
 * before this page even loads, so this code path never runs.
 */

interface FirebaseEnv {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
}

function readFirebaseEnv(): FirebaseEnv | null {
  const env = import.meta.env;
  const apiKey = env.VITE_FIREBASE_API_KEY;
  const authDomain = env.VITE_FIREBASE_AUTH_DOMAIN;
  const projectId = env.VITE_FIREBASE_PROJECT_ID;
  const appId = env.VITE_FIREBASE_APP_ID;

  if (!apiKey || !authDomain || !projectId || !appId) {
    if (env.DEV) {
      console.warn(
        '[firebase] Missing VITE_FIREBASE_* env vars. Auth-action flows will fail. ' +
          'See web-frontend/.env.example.',
      );
    }
    return null;
  }
  return { apiKey, authDomain, projectId, appId };
}

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;

export function getFirebaseAuth(): Auth | null {
  if (_auth) return _auth;
  const cfg = readFirebaseEnv();
  if (!cfg) return null;
  _app = initializeApp(cfg);
  _auth = getAuth(_app);
  return _auth;
}
